import { useState, useEffect, useRef } from "react";
import { C, S, BASE } from "../constants";
import { fmtUAH } from "../utils";
import Img from "../components/Img";

function fmtNum(n) {
  if (!n) return "0";
  return Number(n).toLocaleString("uk-UA");
}

function CpoCell({ cpo }) {
  if (!cpo) return <span style={{ color: C.text4 }}>—</span>;
  const color = cpo < 15 ? C.green : cpo < 25 ? C.yellow : C.pink;
  return <span style={{ color, fontWeight: 700 }}>{cpo.toFixed(2)} ₴</span>;
}

function StatusBadge({ status }) {
  if (!status) return null;
  const active = status.toLowerCase().includes("актив");
  return (
    <span style={{
      fontSize: 10, padding: "2px 6px", borderRadius: 4,
      background: active ? "rgba(52,211,153,.15)" : "rgba(148,163,184,.1)",
      color: active ? C.green : C.text3,
    }}>{status}</span>
  );
}

function KpiCard({ label, value, sub, color }) {
  return (
    <div style={{ ...S.card, padding: "12px 16px", flex: 1, minWidth: 120 }}>
      <div style={{ fontSize: 10, color: C.text4, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: color || C.text1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: C.text3, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function CampaignRow({ camp, token, onDelete }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = () => {
    if (!open && !items) {
      setLoading(true);
      fetch(`${BASE}/api/ads/${camp.id}`, { headers: { Authorization: "Bearer " + token } })
        .then(r => r.ok ? r.json() : null)
        .then(d => { setItems(d?.items || []); setLoading(false); })
        .catch(() => setLoading(false));
    }
    setOpen(o => !o);
  };

  const ctr = camp.impressions > 0 ? (camp.clicks / camp.impressions * 100).toFixed(2) : "0";

  return (
    <div style={{ ...S.card, marginBottom: 8, padding: 0, overflow: "hidden" }}>
      {/* Campaign header */}
      <div
        onClick={toggle}
        style={{
          display: "flex", alignItems: "center", gap: 12, padding: "12px 16px",
          cursor: "pointer", userSelect: "none",
          borderBottom: open ? `1px solid ${C.border}` : "none",
        }}
      >
        <span style={{ color: C.text3, fontSize: 12, transform: open ? "rotate(90deg)" : "none", display: "inline-block", transition: "transform .15s" }}>▶</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {camp.file_name}
          </div>
          <div style={{ fontSize: 11, color: C.text3, marginTop: 2 }}>
            Імпортовано: {camp.imported_at ? new Date(camp.imported_at).toLocaleDateString("uk-UA") : "—"}
          </div>
        </div>
        <div style={{ display: "flex", gap: 20, fontSize: 12 }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ color: C.text4, fontSize: 10 }}>Оголошень</div>
            <div style={{ color: C.text1, fontWeight: 700 }}>{camp.total}</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ color: C.text4, fontSize: 10 }}>Покази</div>
            <div style={{ color: C.text2 }}>{fmtNum(camp.impressions)}</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ color: C.text4, fontSize: 10 }}>CTR</div>
            <div style={{ color: C.text2 }}>{ctr}%</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ color: C.text4, fontSize: 10 }}>Замовлення</div>
            <div style={{ color: C.blue, fontWeight: 700 }}>{fmtNum(camp.orders)}</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ color: C.text4, fontSize: 10 }}>Витрати</div>
            <div style={{ color: C.yellow, fontWeight: 700 }}>{fmtUAH(camp.spend)}</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ color: C.text4, fontSize: 10 }}>CPO</div>
            <CpoCell cpo={camp.cpo} />
          </div>
        </div>
        <button
          onClick={e => { e.stopPropagation(); if (window.confirm(`Видалити ${camp.file_name}?`)) onDelete(camp.id); }}
          style={{ background: "none", border: "none", color: C.text4, cursor: "pointer", fontSize: 14, padding: "0 4px" }}
        >✕</button>
      </div>

      {/* Expanded items table */}
      {open && (
        <div style={{ overflowX: "auto" }}>
          {loading && <div style={{ padding: 20, textAlign: "center", color: C.text3 }}>Завантаження...</div>}
          {!loading && items && (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  {["Товар", "Категорія", "Покази", "Кліки", "CTR", "Замовлення", "Витрати", "CPO", "CPC", "Ставка", "Статус"].map(h => (
                    <th key={h} style={{ ...S.th(false), textAlign: h === "Товар" ? "left" : "right", padding: "8px 10px", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map(item => {
                  const dimmed = item.orders === 0;
                  return (
                    <tr key={item.id} style={{ borderBottom: `1px solid ${C.border}`, opacity: dimmed ? 0.45 : 1 }}>
                      <td style={{ ...S.td, maxWidth: 300, padding: "8px 10px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          {item.product_image && <Img src={item.product_image} size={24} />}
                          <div style={{ minWidth: 0 }}>
                            <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: C.text1, maxWidth: 260 }}>
                              {item.product_name || item.ad_name}
                            </div>
                            {!item.product_name && <div style={{ fontSize: 10, color: C.text4 }}>не зіставлено</div>}
                          </div>
                        </div>
                      </td>
                      <td style={{ ...S.td, textAlign: "right", color: C.text3 }}>{item.category_name || "—"}</td>
                      <td style={{ ...S.td, textAlign: "right" }}>{fmtNum(item.impressions)}</td>
                      <td style={{ ...S.td, textAlign: "right" }}>{fmtNum(item.clicks)}</td>
                      <td style={{ ...S.td, textAlign: "right", color: C.text2 }}>{item.ctr}%</td>
                      <td style={{ ...S.td, textAlign: "right", color: C.blue, fontWeight: item.orders > 0 ? 700 : 400 }}>{item.orders}</td>
                      <td style={{ ...S.td, textAlign: "right", color: C.yellow }}>{fmtUAH(item.spend)}</td>
                      <td style={{ ...S.td, textAlign: "right" }}><CpoCell cpo={item.cpo} /></td>
                      <td style={{ ...S.td, textAlign: "right", color: C.text3 }}>{item.cpc ? item.cpc.toFixed(2) + " ₴" : "—"}</td>
                      <td style={{ ...S.td, textAlign: "right", color: C.text3 }}>{item.bid ? item.bid.toFixed(2) + " ₴" : "—"}</td>
                      <td style={{ ...S.td, textAlign: "right" }}><StatusBadge status={item.status} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdsPage({ token }) {
  const [campaigns, setCampaigns] = useState([]);
  const [summary, setSummary] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState(null);
  const fileRef = useRef();

  const load = () => {
    fetch(`${BASE}/api/ads`, { headers: { Authorization: "Bearer " + token } })
      .then(r => r.ok ? r.json() : [])
      .then(d => setCampaigns(Array.isArray(d) ? d : []))
      .catch(() => {});
    fetch(`${BASE}/api/ads/summary`, { headers: { Authorization: "Bearer " + token } })
      .then(r => r.ok ? r.json() : null)
      .then(d => setSummary(d))
      .catch(() => {});
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadMsg(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await fetch(`${BASE}/api/ads/import`, {
        method: "POST",
        headers: { Authorization: "Bearer " + token },
        body: fd,
      });
      const d = await r.json();
      if (d.ok) {
        setUploadMsg({ type: "ok", text: `Імпортовано ${d.total} рядків, зіставлено ${d.matched} товарів` });
        load();
      } else {
        setUploadMsg({ type: "err", text: d.detail || "Помилка" });
      }
    } catch (err) {
      setUploadMsg({ type: "err", text: String(err) });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDelete = (id) => {
    fetch(`${BASE}/api/ads/${id}`, {
      method: "DELETE",
      headers: { Authorization: "Bearer " + token },
    }).then(() => load()).catch(() => {});
  };

  return (
    <div style={{ padding: "16px 20px", maxWidth: 1200 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text1 }}>Реклама</h2>
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          style={{
            padding: "6px 14px", borderRadius: 6, fontSize: 12, cursor: "pointer",
            background: C.blue, color: "#fff", border: "none", fontWeight: 600,
            opacity: uploading ? 0.6 : 1,
          }}
        >
          {uploading ? "Завантаження..." : "▲ Завантажити XLS"}
        </button>
        <input ref={fileRef} type="file" accept=".xls,.xlsx" style={{ display: "none" }} onChange={handleUpload} />
        {uploadMsg && (
          <span style={{ fontSize: 12, color: uploadMsg.type === "ok" ? C.green : C.pink }}>
            {uploadMsg.text}
          </span>
        )}
      </div>

      {/* KPI summary */}
      {summary && (summary.impressions > 0 || campaigns.length > 0) && (
        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          <KpiCard label="Загальні витрати" value={fmtUAH(summary.spend)} color={C.yellow} />
          <KpiCard label="Замовлень" value={fmtNum(summary.orders)} color={C.blue} />
          <KpiCard label="Середній CPO" value={summary.cpo ? summary.cpo.toFixed(2) + " ₴" : "—"} color={summary.cpo < 15 ? C.green : summary.cpo < 25 ? C.yellow : C.pink} />
          <KpiCard label="Кліки" value={fmtNum(summary.clicks)} sub={`CTR ${summary.ctr}%`} />
          <KpiCard label="Покази" value={fmtNum(summary.impressions)} />
        </div>
      )}

      {/* CPO legend */}
      <div style={{ display: "flex", gap: 16, marginBottom: 12, fontSize: 11, color: C.text3 }}>
        <span>CPO:</span>
        <span style={{ color: C.green }}>● &lt; 15 ₴ — добре</span>
        <span style={{ color: C.yellow }}>● 15–25 ₴ — середньо</span>
        <span style={{ color: C.pink }}>● &gt; 25 ₴ — дорого</span>
      </div>

      {/* Campaigns list */}
      {campaigns.length === 0 ? (
        <div style={{ ...S.card, padding: 40, textAlign: "center", color: C.text3 }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📢</div>
          <div>Немає завантажених кампаній</div>
          <div style={{ fontSize: 12, color: C.text4, marginTop: 4 }}>
            Вивантажте XLS з Rozetka Seller Center → Реклама → Звіт
          </div>
        </div>
      ) : (
        campaigns.map(c => (
          <CampaignRow key={c.id} camp={c} token={token} onDelete={handleDelete} />
        ))
      )}
    </div>
  );
}
